import React from 'react';
import type { MessengerMessage } from '@/api/client';
import { DirectMessageList } from '@/components/Messenger/direct/DirectMessageList';
import { MessengerMessagesLoader } from '@/components/Messenger/shared/chrome/MessengerMessagesLoader';
import { useDirectThreadMessages } from '@/hooks/useDirectThreadMessages';
import { MESSENGER_SIDEBAR_SEARCH_HIGHLIGHT_EMPTY } from '@/constants/messengerSearchHighlight';

export interface DirectThreadMessageFeedProps {
  threadId: string;
  currentUserId: string | null;
  loading: boolean;
  viewportUnreadAnchorId: string | null;
  selectionMode: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (msg: MessengerMessage) => void;
  onToggleMessageLike: (msg: MessengerMessage) => void;
  onRetry: (optimisticId: string) => void;
  onReply: (msg: MessengerMessage) => void;
  onForward?: (msg: MessengerMessage) => void;
  onDelete: (msg: MessengerMessage) => void;
  onStartEdit: (msg: MessengerMessage) => void;
  onPin: (msg: MessengerMessage) => void;
  onUnpin: (msg: MessengerMessage) => void;
  onJumpToMessage: (messageId: string) => void;
  variant?: 'direct' | 'group';
  isSavedThread?: boolean;
  sidebarSearchHighlightWords?: readonly string[];
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

function propsEqual(prev: DirectThreadMessageFeedProps, next: DirectThreadMessageFeedProps): boolean {
  return (
    prev.threadId === next.threadId &&
    prev.currentUserId === next.currentUserId &&
    prev.loading === next.loading &&
    prev.viewportUnreadAnchorId === next.viewportUnreadAnchorId &&
    prev.selectionMode === next.selectionMode &&
    setsEqual(prev.selectedIds, next.selectedIds) &&
    prev.onToggleSelect === next.onToggleSelect &&
    prev.onToggleMessageLike === next.onToggleMessageLike &&
    prev.onRetry === next.onRetry &&
    prev.onReply === next.onReply &&
    prev.onForward === next.onForward &&
    prev.onDelete === next.onDelete &&
    prev.onStartEdit === next.onStartEdit &&
    prev.onPin === next.onPin &&
    prev.onUnpin === next.onUnpin &&
    prev.onJumpToMessage === next.onJumpToMessage &&
    prev.variant === next.variant &&
    prev.isSavedThread === next.isSavedThread &&
    prev.sidebarSearchHighlightWords === next.sidebarSearchHighlightWords
  );
}

const DirectThreadMessageFeedInner = React.memo(function DirectThreadMessageFeedInner({
  threadId,
  currentUserId,
  loading,
  viewportUnreadAnchorId,
  selectionMode,
  selectedIds,
  onToggleSelect,
  onToggleMessageLike,
  onRetry,
  onReply,
  onForward,
  onDelete,
  onStartEdit,
  onPin,
  onUnpin,
  onJumpToMessage,
  variant = 'direct',
  isSavedThread = false,
  sidebarSearchHighlightWords = MESSENGER_SIDEBAR_SEARCH_HIGHLIGHT_EMPTY,
}: DirectThreadMessageFeedProps) {
  const { messages } = useDirectThreadMessages(threadId);

  return (
    <>
      {loading && messages.length === 0 ? (
        <MessengerMessagesLoader />
      ) : (
        <DirectMessageList
          messages={messages}
          currentUserId={currentUserId}
          animationContextId={`thread:${threadId}`}
          viewportUnreadAnchorId={viewportUnreadAnchorId}
          selectionMode={selectionMode}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
          onToggleMessageLike={onToggleMessageLike}
          onRetry={onRetry}
          onReply={onReply}
          onForward={onForward}
          onDelete={onDelete}
          onStartEdit={onStartEdit}
          onPin={onPin}
          onUnpin={onUnpin}
          onJumpToMessage={onJumpToMessage}
          showIncomingAuthorName={variant === 'group'}
          relaxDeleteAgeLimit={isSavedThread}
          threadVariant={variant}
          sidebarSearchHighlightWords={sidebarSearchHighlightWords}
        />
      )}
    </>
  );
}, propsEqual);

export const DirectThreadMessageFeed = DirectThreadMessageFeedInner;
